import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ImageIcon, X } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";
import { resolveImg } from "@/lib/api";

export default function Gallery() {
  const { gallery, settings } = useData();
  const [filter, setFilter] = useState("Tutte");
  const [lightbox, setLightbox] = useState(null);

  const categories = useMemo(() => {
    const set = new Set(gallery.map((g) => g.categoria).filter(Boolean));
    return ["Tutte", ...Array.from(set)];
  }, [gallery]);
  const filtered = filter === "Tutte" ? gallery : gallery.filter((g) => g.categoria === filter);

  return (
    <>
      <Seo title="Galleria" description="Galleria fotografica dei lavori realizzati da Animaverde: giardini, potature, manutenzione e riqualificazione del verde in provincia di Varese." path="/galleria" />
      <PageHero
        overline="Galleria"
        title="Le foto dei nostri lavori"
        subtitle="Una raccolta di immagini dei nostri interventi sul verde. La galleria viene aggiornata con le fotografie reali dei lavori realizzati."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Galleria</span></>}
      />

      <section className="bg-av-black py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          {gallery.length === 0 ? (
            <div className="flex flex-col items-center text-center py-16">
              <span className="w-16 h-16 rounded-full bg-av-green-dark/50 text-av-lime flex items-center justify-center mb-6" style={{ backgroundColor: "rgba(22,56,29,0.5)" }}><ImageIcon size={28} /></span>
              <h2 className="font-display text-3xl text-av-white">La galleria sarà presto disponibile</h2>
              <p className="mt-3 text-av-gray max-w-lg">Stiamo raccogliendo le fotografie dei nostri lavori. Nel frattempo puoi contattarci per richiedere un sopralluogo gratuito.</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contatti" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">Richiedi un sopralluogo</Link>
                <WhatsAppButton variant="outline" />
              </div>
            </div>
          ) : (
            <>
              {categories.length > 1 && (
                <div className="flex flex-wrap gap-2.5 mb-10">
                  {categories.map((c) => (
                    <button key={c} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${filter === c ? "bg-av-bright text-av-black" : "border border-white/20 text-av-white/80 hover:border-av-lime hover:text-av-lime"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5">
                {filtered.map((g) => (
                  <Reveal key={g.id}>
                    <button onClick={() => setLightbox(g)} data-testid={`gallery-item-${g.id}`} className="group block w-full overflow-hidden rounded-2xl">
                      <img src={resolveImg(g.immagine)} alt={g.alt || g.titolo || "Lavoro Animaverde"} loading="lazy"
                           className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </button>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="gallery-lightbox">
            <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
            <img src={resolveImg(lightbox.immagine)} alt={lightbox.alt || lightbox.titolo || ""} className="max-h-[85vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
